import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LocizeBackend from 'i18next-locize-backend';

import { opTime } from 'utils/dates';
import { locizeOptions } from 'i18n/locize';

import 'dayjs/locale/de';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/it';
import 'dayjs/locale/pt';

import en from './locales/en/translation.json';
import es from './locales/es/translation.json';
import de from './locales/de/translation.json';
import it from './locales/it/translation.json';
import fr from './locales/fr/translation.json';
import pt from './locales/pt/translation.json';

const supportedLangs = ['en', 'es', 'de', 'it', 'fr', 'pt'];
const browserLang = navigator.language.split('-')[0];
const savedLang =
  localStorage.getItem('i18nextLng') ||
  (supportedLangs.includes(browserLang) ? browserLang : 'en');

const normalizeLanguage = (language?: string) =>
  language?.split('-')[0].toLowerCase() || 'en';

const syncDayjsLocale = (language?: string) => {
  const normalizedLanguage = normalizeLanguage(language);
  opTime.locale(
    supportedLangs.includes(normalizedLanguage) ? normalizedLanguage : 'en',
  );
};

// i18n instance for the main app's shared translations (Locize). Used as a
// fallback below when a key is missing from visitorApp's own translations.
const mainAppI18n = i18n.createInstance();
mainAppI18n
  .use(initReactI18next)
  .use(LocizeBackend)
  .init({
    lng: savedLang, // Use the same initial language as visitor i18n
    fallbackLng: 'en',
    keySeparator: false,
    nsSeparator: false,
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
    backend: locizeOptions,
  });

i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    es: { translation: es },
    de: { translation: de },
    it: { translation: it },
    fr: { translation: fr },
    pt: { translation: pt },
  },
  lng: savedLang,
  fallbackLng: 'en',
  keySeparator: false,
  nsSeparator: false,
  interpolation: {
    escapeValue: false,
  },
});

// Fall back to the main app's shared translations when a key is missing
// from visitorApp's own translations. i18next's `parseMissingKeyHandler`
// isn't used here because it doesn't receive the interpolation values
// (e.g. counts, names) needed to fill in the translated text.
const originalT = i18n.t.bind(i18n);
i18n.t = ((key: string, options?: Record<string, unknown>) => {
  if (typeof key === 'string' && !i18n.exists(key, options as never)) {
    // Only fallback to mainAppI18n if it has loaded at least one language.
    // This prevents generating unnecessary Locize "missing" requests during
    // initial page load when mainAppI18n hasn't finished loading yet.
    if (
      mainAppI18n.isInitialized &&
      mainAppI18n.hasLoadedNamespace('translation')
    ) {
      return mainAppI18n.t(key, options as never);
    }
    // If mainAppI18n isn't ready yet, return the key as-is rather than
    // triggering a Locize missing request. Once mainAppI18n loads, the
    // 'loaded' event below will trigger a re-render with the proper translation.
    return key;
  }
  return originalT(key, options as never);
}) as typeof i18n.t;

syncDayjsLocale(savedLang);
i18n.on('languageChanged', syncDayjsLocale);

// Sync language changes
i18n.on('languageChanged', (lng) => {
  mainAppI18n.changeLanguage(lng);
});

// Keep localStorage's i18nextLng in sync so that shared helpers like
// selectCurrentIdentityLanguage (used by useOpQuery/useOpMutation to set the
// Accept-Language header) reflect visitorApp's actual displayed language,
// since visitorApp doesn't dispatch SET_IDENTITY_INFO (no AuthenticatedContainer).
i18n.on('languageChanged', (lng) => {
  localStorage.setItem('i18nextLng', lng);
});

// mainAppI18n's translations load asynchronously. Once they're ready,
// trigger a re-render so any text resolved via the fallback above updates
// from the raw key to the translated text.
mainAppI18n.on('loaded', () => {
  i18n.emit('languageChanged', i18n.language);
});

export default i18n;
